import { makeAutoObservable, runInAction } from 'mobx'
import type { IDropdownItem } from 'shared/ui'
import {
  ChatbotBookingType,
  type ChatbotBooking,
  type IChatbotField,
  type IBookingType,
} from 'entities/Chatbot'
import { formatWidgetMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import { type InstructionsSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsSectionStore'
import type { BookingSectionStore } from './bookingSectionStore'
import { GoogleCalendarPickerStore } from './googleCalendarPickerStore'

const TOTAL_STEPS = 4
const GOOGLE_TOTAL_STEPS = 6

export const STEP_HEADERS = [
  'What scheduling app do you use?',
  'Which event link should the agent use?',
  'How should customers book meetings?',
  'Should the agent handle reminders, cancellations, and reschedules automatically?',
]

export const GOOGLE_STEP_HEADERS = [
  'What scheduling app do you use?',
  'Which calendar should the agent use for booking meetings?',
  'Which days are you available for meetings?',
  'What hours are you available for meetings?',
  'How long should each meeting be?',
  'Should the agent handle reminders, cancellations, and reschedules automatically?',
]

export type IWidgetOption = { id: IBookingType; label: string; tooltip?: string }

export const BOOKING_METHOD_OPTIONS: IWidgetOption[] = [
  {
    id: ChatbotBookingType.TimeChat,
    label: 'Chat to book a time',
    tooltip: 'The AI Agent will text back and forth with the contact to schedule a time',
  },
  {
    id: ChatbotBookingType.ShareLink,
    label: 'Share booking link',
    tooltip: 'The AI Agent will share the booking link so the contact can choose a time',
  },
]
export const BOOKING_REMINDERS_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export class BookingWidgetStore {
  step = 1
  focusedIndex = 0
  search = ''
  isSelectingIntegration = false
  isSkipped = false
  localSelectedConnection: IDropdownItem | null = null
  localEventLink: IDropdownItem | null = null
  localBookingType: IBookingType
  localSmartBookingEnabled: boolean
  localBookingFields: IChatbotField[] | null = null
  private _localGoogleCalendar: ChatbotBooking['googleCalendar'] = null
  private _localAvailability: ChatbotBooking['availability'] = null
  private _localDurations: ChatbotBooking['durations'] = null
  readonly googleCalendarPickerStore: GoogleCalendarPickerStore

  constructor(
    readonly sectionStore: BookingSectionStore,
    readonly chatStore: ChatStore,
    readonly instructionsSectionStore: InstructionsSectionStore
  ) {
    this.localBookingType = sectionStore.bookingType
    this.localSmartBookingEnabled = sectionStore.smartBookingEnabled
    this.googleCalendarPickerStore = new GoogleCalendarPickerStore(
      sectionStore.googleCalendarIntegrationStore,
      this
    )
    makeAutoObservable(this)
  }

  get googleCalendar() {
    return this._localGoogleCalendar
  }

  get availability() {
    return this._localAvailability
  }

  get durations() {
    return this._localDurations
  }

  setGoogleCalendar = (value: ChatbotBooking['googleCalendar']) => {
    this._localGoogleCalendar = value
  }

  setAvailability = (value: ChatbotBooking['availability']) => {
    this._localAvailability = value
  }

  setDurations = (value: ChatbotBooking['durations']) => {
    this._localDurations = value
  }

  get isLocalHubspotSelected(): boolean {
    if (!this.localSelectedConnection) return false
    return this.sectionStore.isHubspotConnection(this.localSelectedConnection)
  }

  get isLocalGoogleCalendarSelected() {
    if (!this.localSelectedConnection) return false
    return this.sectionStore.isGoogleCalendarConnection(this.localSelectedConnection)
  }

  get totalSteps() {
    return this.isLocalGoogleCalendarSelected ? GOOGLE_TOTAL_STEPS : TOTAL_STEPS
  }

  get isLastStep() {
    return this.step === this.totalSteps
  }

  get showsNextStepAction() {
    return this.isLocalGoogleCalendarSelected && this.step >= 2 && this.step <= 5
  }

  get isNextStepDisabled() {
    const { availabilityDays, durations, selectedCalendarItem } = this.googleCalendarPickerStore

    if (this.step === 2) return !selectedCalendarItem
    if (this.step === 3) return availabilityDays.size === 0
    if (this.step === 5) return durations.length === 0
    return false
  }

  get stepHeaders() {
    return this.isLocalGoogleCalendarSelected ? GOOGLE_STEP_HEADERS : STEP_HEADERS
  }

  get submitMessage(): string {
    if (this.isSkipped)
      return formatWidgetMessage(this.stepHeaders.map((header) => ({ q: header, a: 'Skip' })))

    if (this.isLocalGoogleCalendarSelected) {
      const {
        availabilityDaysOrder,
        availabilityDays,
        getAvailabilityDayLabel,
        availabilityFrom,
        availabilityTo,
        durations,
        selectedCalendarItem,
      } = this.googleCalendarPickerStore
      const daysLabel = availabilityDaysOrder
        .filter((day) => availabilityDays.has(day))
        .map((day) => getAvailabilityDayLabel(day))
        .join(', ')
      const durationsLabel = durations.map((minutes) => `${minutes} min`).join(', ')

      return formatWidgetMessage([
        { q: this.stepHeaders[0], a: this.localSelectedConnection?.label ?? '' },
        { q: this.stepHeaders[1], a: selectedCalendarItem?.label ?? '' },
        { q: this.stepHeaders[2], a: daysLabel },
        { q: this.stepHeaders[3], a: `${availabilityFrom} - ${availabilityTo}` },
        { q: this.stepHeaders[4], a: durationsLabel },
        { q: this.stepHeaders[5], a: this.localSmartBookingEnabled ? 'Yes' : 'No' },
      ])
    }

    return formatWidgetMessage([
      { q: this.stepHeaders[0], a: this.localSelectedConnection?.label ?? '' },
      { q: this.stepHeaders[1], a: this.localEventLink?.label ?? '' },
      {
        q: this.stepHeaders[2],
        a:
          BOOKING_METHOD_OPTIONS.find((option) => option.id === this.localBookingType)?.label ?? '',
      },
      { q: this.stepHeaders[3], a: this.localSmartBookingEnabled ? 'Yes' : 'No' },
    ])
  }

  get currentListLength() {
    if (this.isLocalGoogleCalendarSelected) {
      switch (this.step) {
        case 1:
          return this.sectionStore.connectionList.length
        case 6:
          return BOOKING_REMINDERS_OPTIONS.length
        default:
          return 0
      }
    }

    switch (this.step) {
      case 1:
        return this.sectionStore.connectionList.length
      case 2:
        return this.sectionStore.meetingsList.length
      case 3:
        return this.isLocalHubspotSelected ? 1 : BOOKING_METHOD_OPTIONS.length
      default:
        return BOOKING_REMINDERS_OPTIONS.length
    }
  }

  get focusableItems() {
    if (this.isLocalGoogleCalendarSelected) return null
    return this.step === 2 ? this.sectionStore.meetingsList : null
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    const raw = Math.min(this.focusedIndex, this.currentListLength - 1)
    const items = this.focusableItems
    if (items?.[raw]?.disabled) {
      const firstEnabled = items.findIndex((item) => !item.disabled)
      return firstEnabled === -1 ? raw : firstEnabled
    }
    return raw
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    let next = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
    const items = this.focusableItems
    if (items) {
      const isMeetingsStep = !this.isLocalGoogleCalendarSelected && this.step === 2
      const loadMoreOnForwardEnd = () => {
        if (isMeetingsStep && delta > 0 && this.sectionStore.hasMoreMeetings) {
          this.sectionStore.loadMoreMeetings()
        }
      }
      while (items[next]?.disabled) {
        const candidate = next + delta
        if (candidate < 0 || candidate >= this.currentListLength) {
          loadMoreOnForwardEnd()
          return
        }
        next = candidate
      }
      if (next >= this.currentListLength - 1) {
        loadMoreOnForwardEnd()
      }
    }
    this.focusedIndex = next
  }

  setSearch = (value: string) => {
    this.search = value
    this.focusedIndex = 0
    if (this.step === 2 && !this.isLocalGoogleCalendarSelected) this.sectionStore.setSearch(value)
  }

  private resetStepState = () => {
    this.focusedIndex = 0
    this.search = ''
    this.sectionStore.setSearch('')
  }

  private advanceStep = () => {
    this.step++
    this.resetStepState()
  }

  private selectedIndexForStep = (step: number): number => {
    if (step === 1) {
      const id = (this.localSelectedConnection ?? this.sectionStore.selectedConnectionItem)?.id
      if (id === undefined) return 0
      return Math.max(
        0,
        this.sectionStore.connectionList.findIndex((item) => item.id === id)
      )
    }

    if (this.isLocalGoogleCalendarSelected) return 0

    switch (step) {
      case 2: {
        const id = this.localEventLink?.id
        if (id === undefined) return 0
        return Math.max(
          0,
          this.sectionStore.meetingsList.findIndex((item) => item.id === id)
        )
      }
      case 3: {
        return Math.max(
          0,
          BOOKING_METHOD_OPTIONS.findIndex((o) => o.id === this.localBookingType)
        )
      }
      default:
        return 0
    }
  }

  goBack = () => {
    this.step = Math.max(1, this.step - 1)
    this.search = ''
    this.sectionStore.setSearch('')
    this.focusedIndex = this.selectedIndexForStep(this.step)
  }

  reset = () => {
    if (
      this.isLocalGoogleCalendarSelected &&
      !this.sectionStore.isGoogleCalendarSelected &&
      !this.chatStore.hasPendingCallback
    ) {
      this.sectionStore.googleCalendarIntegrationStore.dispose()
    }

    this.step = 1
    this.resetStepState()
    this.localSelectedConnection = null
    this.localEventLink = null
    this.localBookingType = this.sectionStore.bookingType
    this.localSmartBookingEnabled = this.sectionStore.smartBookingEnabled
    this.localBookingFields = null
    this.googleCalendarPickerStore.resetErrors()
    this._localGoogleCalendar = null
    this._localAvailability = null
    this._localDurations = null
    this.googleCalendarPickerStore.restoreAvailabilityTime()
  }

  private selectOnStep1 = async (): Promise<void> => {
    const item = this.sectionStore.connectionList[this.safeIndex]
    if (!item) return

    this.isSelectingIntegration = true
    try {
      await this.sectionStore.setConnection(item, true)
    } finally {
      runInAction(() => {
        this.isSelectingIntegration = false
      })
    }

    if (this.sectionStore.isIntegrationReadyForWidget(item)) {
      const isGoogleCalendar = this.sectionStore.isGoogleCalendarConnection(item)

      if (this.localSelectedConnection?.id !== item.id) {
        this.googleCalendarPickerStore.resetBookingData()
      }
      runInAction(() => {
        this.localSelectedConnection = item
        this.localEventLink = null
        if (isGoogleCalendar) {
          this.localBookingType = ChatbotBookingType.TimeChat
        }
      })
      this.advanceStep()
    }
  }

  private selectOnStep2 = () => {
    const item = this.sectionStore.meetingsList[this.safeIndex]
    this.localEventLink = item
    if (this.localSelectedConnection) {
      void this.sectionStore
        .fetchWidgetBookingFields(this.localSelectedConnection, item)
        .then((fields) => {
          runInAction(() => {
            this.localBookingFields = fields
          })
        })
    }
    this.advanceStep()
  }

  private selectOnStep3 = () => {
    const item = BOOKING_METHOD_OPTIONS[this.safeIndex]
    this.localBookingType = item.id
    this.advanceStep()
  }

  private submitStep4 = () => {
    const option = BOOKING_REMINDERS_OPTIONS[this.safeIndex]
    this.localSmartBookingEnabled = option.id === 'yes'
  }

  selectAt = async (index: number) => {
    this.focusedIndex = index
    await this.handleSelect()
  }

  handleSelect = async () => {
    if (this.isLocalGoogleCalendarSelected) {
      if (this.step === 1) await this.selectOnStep1()
      return
    }

    switch (this.step) {
      case 1:
        await this.selectOnStep1()
        break
      case 2:
        this.selectOnStep2()
        break
      case 3:
        this.selectOnStep3()
        break
    }
  }

  goNext = () => {
    if (this.isLastStep || !this.showsNextStepAction || this.isNextStepDisabled) return

    const isValid =
      this.step === 2
        ? this.googleCalendarPickerStore.validateCalendar()
        : this.step === 4
        ? this.googleCalendarPickerStore.validateAvailabilityTime()
        : true

    if (!isValid) return
    this.advanceStep()
  }

  handleClose = () => {
    this.isSkipped = true
    const message = this.submitMessage

    this.chatStore.sendMessage(message)
  }

  handleSubmit = () => {
    this.submitStep4()
    const message = this.submitMessage
    const localSelectedConnection = this.localSelectedConnection
    const localEventLink = this.localEventLink
    const localBookingType = this.localBookingType
    const localSmartBookingEnabled = this.localSmartBookingEnabled
    const isGoogleDraftActive = this.isLocalGoogleCalendarSelected
    const localGoogleCalendar = this._localGoogleCalendar
    const localAvailability = this._localAvailability
    const localDurations = this._localDurations

    this.chatStore.setLocalWidgetState({
      settings: {
        book_appointment_enabled: true,
        book_appointment_type: localBookingType,
      },
      ...(this.localBookingFields && { booking_fields: this.localBookingFields }),
    })

    this.chatStore.sendMessage(message, async () => {
      if (isGoogleDraftActive) {
        this.sectionStore.commitGoogleCalendarSelection(
          localGoogleCalendar,
          localAvailability,
          localDurations
        )
      }
      if (localSelectedConnection) await this.sectionStore.setConnection(localSelectedConnection)
      if (localEventLink) this.sectionStore.setLink(localEventLink)
      this.sectionStore.setBookingType(localBookingType)
      this.sectionStore.setSmartBookingEnabled(localSmartBookingEnabled)
      this.sectionStore.setBookingEnabled(true)
      await this.sectionStore.init()
      this.instructionsSectionStore.setInstructions(this.instructionsSectionStore.instructions)
    })
  }
}
